import { Component, ElementRef, Injector, Input, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmDialogConfig, CrudConfirmDialogComponent, CrudDataSource, CrudDeleteDialogComponent, CrudListComponent, FormStorage, openCrudRowsEditorDialogInAttributeEditorMode, REPOSITORY_FILE_ROWS_TYPE, UserStorage } from '@scisys-uim/uim-common';
import { AssetsUser, BUSINESS_UNIT_TYPE, BusinessUnit, Firearm, FIREARM_TYPE, FirearmAssignment, FIREARM_ASSIGNMENT_TYPE, validateFirearmAbleToGoIntoService, RefCode, FirearmAssignmentNextAction, FirearmAssignmentStorageType, FirearmAssignmentType } from 'assets-model';
import { FirearmAssignmentWithDocumentsDataSource, FirearmAssignmentService, FirearmDocumentService, RefCodeStorage, FirearmDocumentPermission } from 'assets-service';
import { FirearmAssignmentApprovalDialogComponent, FirearmAssignmentApprovalDialogData, FirearmAssignmentApprovalDialogConfigData, FirearmAssignmentApprovalType } from '../dialog/firearm-assignment-approval-dialog.component';
import { FirearmAssignmentCancellationDialogComponent, FirearmAssignmentCancellationDialogConfigData, FirearmAssignmentCancellationDialogData } from '../dialog/firearm-assignment-cancellation-dialog.component';
import { FirearmAssignmentActivationDialogComponent, FirearmAssignmentActivationDialogData, FirearmAssignmentActivationDialogConfigData } from '../dialog/firearm-assignment-activation-dialog.component';
import { FirearmDocumentEditDialogComponent, FirearmDocumentEditDialogConfig, FirearmDocumentRowsComponent, FirearmDocumentRowsComponentMode } from '../../firearm/editor/firearm-document-rows.component';
import { MatSelect } from '@angular/material/select';


@Component({
  selector: 'app-firearm-assignment-pending-action-list',
  templateUrl: './firearm-assignment-pending-action-list.component.html',
  styleUrls: ['./firearm-assignment-pending-action-list.component.scss']
})
export class FirearmAssignmentPendingActionListComponent extends CrudListComponent<FirearmAssignment, number> {

  displayedColumns: string[] = ['firearmAssignmentId', 'assetId', 'businessUnit.buId', 'firearmAssignmentType', 'storageType', 'personEmail', 'firearm.assetId', 'comments', 'lastStateChangeDate', 'lifecycleState', 'nextAction', 'notifyPolice', 'createdBy', 'createdDate', 'buApproved', 'buApprovedRejectedBy', 'buApprovedRejectedDate', 'buApprovalRejectionReason', 'leadApproved', 'leadApprovedRejectedBy', 'leadApprovedRejectedDate', 'leadApprovalRejectionReason', 'actions'];

  disabledColumns: string[] = ['firearmAssignmentId', 'assetId', 'businessUnit.buId', 'comments', 'createdBy', 'createdDate', 'lifecycleState', 'buApproved', 'buApprovedRejectedBy', 'buApprovedRejectedDate', 'buApprovalRejectionReason', 'leadApproved', 'leadApprovedRejectedBy', 'leadApprovedRejectedDate', 'leadApprovalRejectionReason'];

  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  @Input() queryParams: any; // Input for query parameters

  searchForm: FormGroup;

  nextActionSelectionList: RefCode[];
  nextActionFilter: FirearmAssignmentNextAction;
  @ViewChild('nextActionFilterInput') nextActionFilterInput: MatSelect;

  firearmAssignmentTypeSelectionList: RefCode[];
  firearmAssignmentTypeFilter: FirearmAssignmentType;
  @ViewChild('firearmAssignmentTypeFilterInput') firearmAssignmentTypeFilterInput: MatSelect;

  
  storageTypeSelectionList: RefCode[];
  storageTypeFilter: FirearmAssignmentStorageType;
  @ViewChild('storageTypeFilterInput') storageTypeFilterInput: MatSelect;

  assetIdFilter: number;
  @ViewChild('assetIdFilterInput') assetIdFilterInput: ElementRef;

  identityReferenceOrDescriptionFilter: string;
  @ViewChild('identityReferenceOrDescriptionFilterInput') identityReferenceOrDescriptionFilterInput: ElementRef;

  constructor(protected route: ActivatedRoute,
    protected router: Router,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected firearmAssignmentService: FirearmAssignmentService,
    protected firearmDocumentService: FirearmDocumentService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected formStorage: FormStorage,
    protected injector: Injector) {
    super(FIREARM_ASSIGNMENT_TYPE, route, router, userStorage, dialog, firearmAssignmentService, injector);
    this.searchForm = this.fb.group({
      nextAction: [null],
      firearmAssignmentType: [null],
      storageType: [null],
      assetId: [null],
      identityReferenceOrDescription: [null],
    });
  }

  createDataSource(): CrudDataSource<FirearmAssignment> {
    return new FirearmAssignmentWithDocumentsDataSource(this.firearmAssignmentService, this.firearmDocumentService);
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.addSearchOnChangedSelectEventListener(this.nextActionFilterInput);
    this.addSearchOnChangedSelectEventListener(this.firearmAssignmentTypeFilterInput);
    this.addSearchOnChangedSelectEventListener(this.storageTypeFilterInput);
    this.addSearchOnChangedInputEventListener(this.assetIdFilterInput);
    this.addSearchOnChangedInputEventListener(this.identityReferenceOrDescriptionFilterInput);
    this.formStorage.rememberFormChanges(this.searchForm);
  }

  createServiceArguments() {
    return {
      actionPending: true,
      buId: this.eAssetsUser?.businessUnit?.national ? null : this.eAssetsUser?.businessUnit?.buId,
      nextAction: this.nextActionFilter,
      firearmAssignmentType: this.firearmAssignmentTypeFilter,
      storageType: this.storageTypeFilter,
      assetId: this.assetIdFilter,
      identityReferenceOrDescription: this.identityReferenceOrDescriptionFilter,
    };
  }

  ngOnInit() {
    super.ngOnInit();

    // if we disable too soon, the column header display text won't be rendered, and the picker will have code names not display names.
    this.columns.$afterViewInit.subscribe(data => {
      this.columns.setColumnsEnabled(this.disabledColumns, false);
      // reset the defaults for the context so the picker Reset button doesn't re-enable the columns above
      this.columns.resetInitialDisplayColumns();
      // delayed wiring until context set, so that we don't start remembering changes too early
      this.restoreColumnState();
      this.rememberColumnState();
    });

    var anyParams = false;
    if (this.queryParams) {
      if (this.queryParams.nextAction) {
        this.nextActionFilter = this.queryParams.nextAction;
        anyParams = true;
      }
      if (this.queryParams.firearmAssignmentType) {
        this.firearmAssignmentTypeFilter = this.queryParams.firearmAssignmentType;
        anyParams = true;
      }
      if (this.queryParams.storageType) {
        this.storageTypeFilter = this.queryParams.storageType;
        anyParams = true;
      }
      if (this.queryParams.pendingAssignmentAssetId != null) {
        this.assetIdFilter = Number(this.queryParams.pendingAssignmentAssetId);
        anyParams = true;
      }
    }

    this.firearmAssignmentTypeLoadSelectionList();
    this.nextActionLoadSelectionList();
    this.storageTypeLoadSelectionList();
  }

  firearmDisplay(item: Firearm) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return FIREARM_TYPE.description(item);
  }

  businessUnitDisplay(item: BusinessUnit) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return BUSINESS_UNIT_TYPE.description(item);
  }

  allowsDelete(element: FirearmAssignment): boolean {
    if (element.lifecycleState != 'CREATED') {
      return false;
    }
    if (!this.userStorage?.currentUserHasAnyAuthority("DELETE_FIREARM_ASSIGNMENT")) {
      return false;
    }
    return true;
  }

  allowsView(element: FirearmAssignment): boolean {
    if (!this.userStorage?.currentUserHasAnyAuthority("READ_FIREARM_ASSIGNMENT")) {
      return false;
    }
    return true;
  }

  allowsEdit(element: FirearmAssignment): boolean {
    return false;
  }

  allowsApproval(element: FirearmAssignment): boolean {
    if (element.nextAction == 'BU_APPROVAL') {
      if (this.eAssetsUser?.businessUnit?.buId == element?.businessUnit?.buId) {
        return this.userStorage?.currentUserHasAnyAuthority("BU_FIREARM_ASSIGNMENT_APPROVER");
      }
    }
    if (element.nextAction == 'TRANSFER_BU_APPROVAL') {
      if (this.eAssetsUser?.businessUnit?.buId == element?.transferAsset?.businessUnit?.buId
        && this.eAssetsUser.isSameUser(element?.user?.user)) {
        return this.userStorage?.currentUserHasAnyAuthority("BU_FIREARM_ASSIGNMENT_APPROVER");
      }
    }
    if (element.nextAction == 'LEAD_APPROVAL') {
      if (this.userDetails?.username == element?.buApprovedRejectedBy || this.userDetails?.username == element?.transferAsset?.approvedRejectedBy) {
        return false;
      }
      return this.userStorage?.currentUserHasAnyAuthority("LEAD_FIREARM_ASSIGNMENT_APPROVER");
    }
    return false;
  }

  launchApproval(item: FirearmAssignment) {
    var approvalType: FirearmAssignmentApprovalType;
    if (item.nextAction == 'BU_APPROVAL') {
      approvalType = 'BU';
    }
    if (item.nextAction == 'TRANSFER_BU_APPROVAL') {
      approvalType = 'TRANSFER_BU';
    }
    if (item.nextAction == 'LEAD_APPROVAL') {
      approvalType = 'LEAD';
    }
    var ref: MatDialogRef<FirearmAssignmentApprovalDialogComponent, FirearmAssignmentApprovalDialogData> = this.dialog.open(FirearmAssignmentApprovalDialogComponent, {
      panelClass: 'dialog-resizable',
      height: '600px',
      width: '900px',
      data: <FirearmAssignmentApprovalDialogConfigData>{
        firearmAssignment: item,
        approvalType: approvalType
      }
    })
    ref.afterClosed().subscribe(
      result => {
        this.forceSearch();
      }
    );
  }


  allowsCancellation(element: FirearmAssignment): boolean {
    if (element.nextAction == 'BU_ACTIVATION' || element.nextAction == 'CONFIRM_ACTIVATION' || element.nextAction == 'COMPLETE_DISPOSAL') {
      if (this.eAssetsUser?.businessUnit?.buId == element?.businessUnit?.buId || this.userStorage?.currentUserHasAnyAuthority("ACTS_AT_NATIONAL_LEVEL")) {
        return this.userStorage?.currentUserHasAnyAuthority("CANCEL_FIREARM_ASSIGNMENT");
      }
    }
    return false;
  }

  launchCancellation(item: FirearmAssignment) {
    var ref: MatDialogRef<FirearmAssignmentCancellationDialogComponent, FirearmAssignmentCancellationDialogData> = this.dialog.open(FirearmAssignmentCancellationDialogComponent, {
      panelClass: 'dialog-resizable',
      height: '600px',
      width: '900px',
      data: <FirearmAssignmentCancellationDialogConfigData>{
        firearmAssignment: item,
      }
    })
    ref.afterClosed().subscribe(
      result => {
        this.forceSearch();
      }
    );
  }

  currentUserCanActivateAssignment(element: FirearmAssignment): boolean {
    if (this.eAssetsUser?.businessUnit?.buId == element?.businessUnit?.buId || this.userStorage?.currentUserHasAnyAuthority("ACTS_AT_NATIONAL_LEVEL")) {
      return this.userStorage?.currentUserHasAnyAuthority("ACTIVATE_FIREARM_ASSIGNMENT");
    }
    return false;
  }

  currentUserCanStartStorageAssignment(element: FirearmAssignment): boolean {
    // National storage changes the location, not the firearm's owning BU.
    var targetBusinessUnit = element?.location?.businessUnit?.buId == '901'
      ? element?.firearm?.asset?.businessUnit
      : element?.location?.businessUnit;
    if (element.transferAsset != null) {
      targetBusinessUnit = element.transferAsset.businessUnit;
    }
    if (this.eAssetsUser?.businessUnit?.buId == targetBusinessUnit?.buId || this.userStorage?.currentUserHasAnyAuthority("ACTS_AT_NATIONAL_LEVEL")) {
      if (element?.firearmAssignmentType == 'STORAGE' && element?.storageType != 'RETURN') {
        if (this.userStorage?.currentUserHasAnyAuthority("ACTIVATE_FIREARM_ASSIGNMENT")) {
          return element.user?.user == this.eAssetsUser?.username;
        }
        return false;
      }
    }
    return false;
  }

  allowsActivation(element: FirearmAssignment): boolean {
    if (!this.availableActivation(element)) {
      return false;
    }
    if (element.storageType == 'ACQUIRE') {
      var warnings = validateFirearmAbleToGoIntoService(element.firearm);
      return warnings == null || Object.keys(warnings).length == 0;
    }
    if (element.storageType == 'DISPOSE') {
      const disposalDocuments = element.documents?.DISPOSAL;
      return disposalDocuments?.length > 0;
    }
    return true;
  }

  availablePopulate(element: FirearmAssignment): boolean {
    return this.availableActivation(element) && !this.allowsActivation(element) && element.storageType == 'ACQUIRE';
  }

  availableUploadDisposalDocuments(element: FirearmAssignment): boolean {
    return this.availableActivation(element) && !this.allowsActivation(element) && element.storageType == 'DISPOSE';
  }

  availableViewDisposalDocuments(element: FirearmAssignment): boolean {
    return element.storageType == 'DISPOSE' && element.documents?.DISPOSAL?.length > 0;
  }

  openFirearmDisposalDocumentDialog(element: FirearmAssignment) {
    this.firearmDocumentService.getFirearmPermissions(element.firearm.assetId, null).subscribe(permissions => {
      var firearmDocumentsPermissions = <FirearmDocumentPermission[]>permissions;

      var showActions = [];
      var modesAvailable: FirearmDocumentRowsComponentMode[] = [];
      if (this.userDetails.hasAnyAuthority('READ_FIREARM_DOCUMENT')) {
        showActions.push('download');
        modesAvailable.push('LIST_DOCUMENTS');
      }
      if (this.userDetails.hasAnyAuthority('UPLOAD_FIREARM_DOCUMENT')) {
        showActions.push('delete');
        modesAvailable.push('UPLOAD_DOCUMENTS');
      }
      const dialogConfig: FirearmDocumentEditDialogConfig<number> = {
        dialogTitle: "Firearm Disposal Documents",
        showActions: showActions,
        modesAvailable: modesAvailable,
        permissions: firearmDocumentsPermissions,
        firearm: element.firearm,
        uploadDocType: 'DISPOSAL',
        allowSelectUploadDocType: false,
        config: {
          rowsItemComponentType: FirearmDocumentRowsComponent,
          rowsType: REPOSITORY_FILE_ROWS_TYPE,
          readOnly: true
        },
        supportsAdd: false,
        supportsSave: false,
        rows: [],
      };

      const dialogRef = openCrudRowsEditorDialogInAttributeEditorMode(this.dialog, {
        panelClass: ['contract-document-dialog', 'dialog-resizable'],
        height: '80vh',
        minWidth: '60vw',
        //minHeight: '70vh',
        data: dialogConfig
      }, FirearmDocumentEditDialogComponent);

      dialogRef.afterClosed().subscribe(result => {
        this.forceSearch();
      })
    });
  }

  firearmRoute(element: FirearmAssignment): any[] {
    var routeBase = this.userStorage?.currentUserHasAnyAuthority("WRITE_FIREARM") ? FIREARM_TYPE.editRouteBase : FIREARM_TYPE.viewRouteBase;
    return [...routeBase, element.firearm?.assetId];
  }

  viewFirearmRoute(element: FirearmAssignment): any[] {
    return [...FIREARM_TYPE.viewRouteBase, element.firearm?.assetId];
  }

  availableActivation(element: FirearmAssignment): boolean {
    if (element.nextAction == 'BU_ACTIVATION') {
      return this.currentUserCanActivateAssignment(element);
    }
    return false;
  }

  launchActivation(item: FirearmAssignment) {
    var ref: MatDialogRef<FirearmAssignmentActivationDialogComponent, FirearmAssignmentActivationDialogData> = this.dialog.open(FirearmAssignmentActivationDialogComponent, {
      panelClass: 'dialog-resizable',
      height: '600px',
      width: '900px',
      data: <FirearmAssignmentActivationDialogConfigData>{
        firearmAssignment: item,
      }
    })
    ref.afterClosed().subscribe(
      result => {
        this.forceSearch();
      }
    );
  }

  allowsReissueStartToken(element: FirearmAssignment): boolean {
    if (!element.withStartToken) {
      return false;
    }
    if (element.nextAction == 'CONFIRM_ACTIVATION') {
      return this.currentUserCanActivateAssignment(element);
    }
    return false;
  }

  launchReissueStartToken(item: FirearmAssignment) {
    this.firearmAssignmentService.reissueStartToken(item.firearmAssignmentId).subscribe(
      result => {
        this.forceSearch();
      }
    );
  }

  allowsStartAssignment(element: FirearmAssignment): boolean {
    if (element.nextAction == 'CONFIRM_ACTIVATION') {
      // this is a highly privileged case; normal users, even Lead WM, are not expected to have this privilege
      // It's here so that support can do it if necessary (emails are down), and to facilitate testing in email constrained environments.
      if (this.userStorage?.currentUserHasAnyAuthority("START_FIREARM_ASSIGNMENT")) {
        return true;
      }
      // except for storage to external locations, which we allow any WM to start
      if (element.firearmAssignmentType == 'STORAGE' && element.location?.externalLocation && this.userStorage?.currentUserHasAnyAuthority("CREATE_FIREARM_ASSIGNMENT")) {
        return true
      }
      if (element.withStartToken) {
        // the ranger has to do it via the token email
        return false;
      }
      // all other cases actually should be storage to storage, but to prevent a security hole, we'll check:
      const isStorageTransfer = element.firearm?.currentAssignmentFirearmAssignmentType == 'STORAGE' && element.firearmAssignmentType == 'STORAGE';
      return isStorageTransfer && this.currentUserCanStartStorageAssignment(element);
    }
    return false;
  }

  launchStartAssignment(item: FirearmAssignment) {
    this.firearmAssignmentService.startAssignment(item.firearmAssignmentId).subscribe(
      result => {
        this.forceSearch();
      }
    );
  }

  allowsCompleteDisposal(element: FirearmAssignment): boolean {
    return element.nextAction == 'COMPLETE_DISPOSAL'
      && element.firearmAssignmentType == 'STORAGE'
      && element.storageType == 'DISPOSE'
      && this.userStorage?.currentUserHasAnyAuthority("LEAD_FIREARM_ASSIGNMENT_APPROVER");
  }

  launchCompleteDisposal(item: FirearmAssignment) {
    this.openSimpleConfirmationDialog("This will complete the disposal process and mark the firearm as disposed. Please confirm you are satisfied with the uploaded evidence before completing.", "Complete Disposal", "Complete Disposal").afterClosed().subscribe(
      confirm => {
        if (confirm) {
          this.firearmAssignmentService.completeDisposalAssignment(item.firearmAssignmentId).subscribe(
            result => {
              this.forceSearch();
            }
          );
        }
      }
    );
  }

  viewOrEditRouteToItem(element: FirearmAssignment): any[] {
    const keySegments = FIREARM_ASSIGNMENT_TYPE.entityDecorator.toKeyRouteSegments(element);
    if (!keySegments) {
      return;
    }
    var routeBase = FIREARM_ASSIGNMENT_TYPE.viewRouteBase;
    if (this.userStorage?.currentUserHasAnyAuthority(FIREARM_ASSIGNMENT_TYPE.editAuthorities)) {
      routeBase = FIREARM_ASSIGNMENT_TYPE.editRouteBase;
    }
    return [...routeBase, ...keySegments];
  }

  currentUserIsCreator(element: FirearmAssignment): boolean {
    return this.userDetails?.username == element?.createdBy;
  }

  deleteWithConfirmation(item: FirearmAssignment) {
    const self = this;
    var dialogRef: MatDialogRef<CrudConfirmDialogComponent, boolean> = this.openConfirmationDialog(item, {
      data: <ConfirmDialogConfig<FirearmAssignment, number>>{
        title: 'Delete',
        action: 'Delete',
        component: CrudDeleteDialogComponent
      }
    });
    dialogRef.afterClosed().subscribe((confirm: boolean) => {
      if (confirm) {
        self.firearmAssignmentService.delete(item.firearmAssignmentId).subscribe(() => {
          self.loadPage();
        });
      }
    });
  }

  nextActionLoadSelectionList() {
    this.refCodeStorage.getDomainElements('FIREARM_ASSIGNMENT_NEXT_ACTION').subscribe(
      data => {
        this.nextActionSelectionList = data;
      }
    );
  }

  nextActionChanging(value: FirearmAssignmentNextAction, unsetDependents: boolean = true) {}
  
  nextActionChanged(value: FirearmAssignmentNextAction, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  get nextAction() {
    return this.searchForm && this.searchForm.get('nextAction');
  }

  
  firearmAssignmentTypeLoadSelectionList() {
    this.refCodeStorage.getDomainElements('FIREARM_ASSIGNMENT_TYPE').subscribe(
      data => {
        this.firearmAssignmentTypeSelectionList = data;
      }
    );
  }

  firearmAssignmentTypeChanging(value: FirearmAssignmentType, unsetDependents: boolean = true) {}
  
  firearmAssignmentTypeChanged(value: FirearmAssignmentType, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  get firearmAssignmentType() {
    return this.searchForm && this.searchForm.get('firearmAssignmentType');
  }

  storageTypeLoadSelectionList() {
    this.refCodeStorage.getDomainElements('FIREARM_ASSIGNMENT_STORAGE_TYPE').subscribe(
      data => {
        this.storageTypeSelectionList = data;
      }
    );
  }

  storageTypeChanging(value: FirearmAssignmentStorageType, unsetDependents: boolean = true) {}
  
  storageTypeChanged(value: FirearmAssignmentStorageType, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  get storageType() {
    return this.searchForm && this.searchForm.get('storageType');
  }

  get assetId() {
    return this.searchForm && this.searchForm.get('assetId');
  }

  get identityReferenceOrDescription() {
    return this.searchForm && this.searchForm.get('identityReferenceOrDescription');
  }

}



